import { getDocFromServer, getDocsFromServer, query, limit as limitTo } from 'firebase/firestore';
import { hasLiveWriteTo } from './writes';

// ── A default is written only where the server says nothing is (#631) ──────
//
// Several writers fill something in when it looks empty: the expense category
// seed, the built-in lesson categories materialised on a first save, a studio
// record created whole, the Mileage Log's year-turn expenses. This device's
// read can be empty because the collection really is empty; because **the read
// has not answered** (`docs` is `[]` before the first snapshot); or because
// **the answer came from a cache that never held the documents** — a fresh
// listener's first snapshot is routinely `n=0, fromCache=true` (see
// src/hooks/useAddStudentForm.js), and the read store keeps that answer as a
// receipt, so `hasRead` is true for it. A default written in either of the
// last two cases lands on the studio's own documents: a renamed built-in gets
// its default label back, a deleted one comes back, a studio record goes back
// to en-US.
//
// So these writers ask the server, the way #620's time-zone write does
// (`recordStudioTimeZone` in ./useSettings.jsx), with the same two guards:
//
//   * **the server's answer** (`getDocFromServer`/`getDocsFromServer`), which
//     rejects offline rather than falling back to the SDK cache — nothing is
//     written, and the next attempt asks again;
//   * **nothing of ours still owed to the document** (`hasLiveWriteTo`, before
//     and after the read): an edit made offline is in the queue and not on the
//     server, so the server would say "absent" and the default would queue
//     behind the teacher's edit and replace it.
//
// **Not create-only semantics**: the queue has no precondition
// (`core/src/queue.rs` carries set, merge and delete, never "only if absent"),
// and Firestore offers none to a client batch. Left open: a document made on
// another device between the server's answer and the write arriving is
// replaced (#620).
//
// The phones do the same: `ServerConfirmed` in
// android/app/src/main/kotlin/studio/coda/app/data/ServerConfirmed.kt and
// `StudioRepository.absentAtServer` in
// ios/CodaStudio/Data/StudioRepository+ServerConfirmed.swift.

/**
 * The paths of `refs` that the server says do not exist and that this device
 * owes no write to. Rejects when the server cannot be asked.
 */
export async function absentAtServer(refs) {
  const owed = async () => Promise.all(refs.map((ref) => hasLiveWriteTo(ref.path)));
  const before = await owed();
  const snaps = await Promise.all(refs.map((ref, i) => (before[i] ? null : getDocFromServer(ref))));
  const after = await owed();
  return new Set(
    refs
      .filter((ref, i) => !before[i] && !after[i] && !snaps[i].exists())
      .map((ref) => ref.path),
  );
}

/**
 * Whether a server read refused because this client could not reach the
 * server — Firestore's `unavailable`, which `getDocFromServer` throws as
 * "Failed to get document because the client is offline." while the SDK's
 * online state is Offline. That state follows a transient transport error as
 * well as a real outage (#681), so the refusal says only "not known yet": a
 * caller writes nothing and asks again on its next attempt. Any other code
 * (a rules refusal, say) is an answer about the request and is not this.
 */
export function serverUnreachable(err) {
  return err?.code === 'unavailable';
}

/**
 * Whether the server says the collection holds no document at all. Rejects
 * when the server cannot be asked. `limit(1)`: `firestore.rules` refuses a
 * list without one, and one is all the question needs.
 */
export async function collectionEmptyAtServer(collectionRef) {
  const snap = await getDocsFromServer(query(collectionRef, limitTo(1)));
  return snap.empty;
}

/**
 * The sentence a teacher sees when a write that would fill in defaults could
 * not ask the server first. Exported so a test pins it, and so every screen
 * says the same thing.
 */
export const DEFAULTS_NEED_SERVER = 'This needs a connection the first time: the built-in '
  + 'categories are saved only once the server has confirmed this studio has none of its own. '
  + 'Check your connection and try again.';
